/**
 * SEARCH-E4：Journeys 搜尋 URL 參數讀寫（q / filters / sort）。
 * 不寫入 suggestion dropdown / draft composition 等 UI-only state。
 */

export const JOURNEY_SEARCH_URL_KEYS = Object.freeze([
  'q',
  'titleQuery',
  'scope',
  'locationId',
  'destinationKey',
  'budgetMin',
  'budgetMax',
  'currency',
  'minDays',
  'maxDays',
  'sort',
]);

const DEFAULT_SORT = 'LATEST';

function trimOrEmpty(value) {
  if (value == null) return '';
  return String(value).trim();
}

/**
 * 從 URLSearchParams 解析 applied journey search state。
 */
export function readJourneySearchFromParams(searchParams) {
  const params = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(searchParams || '');
  const sortRaw = trimOrEmpty(params.get('sort')).toUpperCase();
  const sort = ['HOT', 'OLDEST', 'LIKES', 'COMMENTS', 'COPIES'].includes(sortRaw) ? sortRaw : DEFAULT_SORT;
  return {
    q: trimOrEmpty(params.get('q')),
    titleQuery: trimOrEmpty(params.get('titleQuery')),
    scope: params.get('scope') === 'FOLLOWING' ? 'FOLLOWING' : 'ALL',
    locationId: params.get('destinationKey') ? '' : trimOrEmpty(params.get('locationId')),
    destinationKey: trimOrEmpty(params.get('destinationKey')),
    budgetMin: trimOrEmpty(params.get('budgetMin')),
    budgetMax: trimOrEmpty(params.get('budgetMax')),
    currency: trimOrEmpty(params.get('currency')).toUpperCase(),
    minDays: trimOrEmpty(params.get('minDays')),
    maxDays: trimOrEmpty(params.get('maxDays')),
    sort,
  };
}

/**
 * 將 applied filters 寫入既有 params（保留 tab 等其他鍵）。
 * 空值刪除對應 key，避免噪音。
 */
export function writeJourneySearchToParams(searchParams, filters) {
  const params = new URLSearchParams(searchParams instanceof URLSearchParams
    ? searchParams
    : String(searchParams || ''));
  const next = filters || {};
  const setOrDelete = (key, value) => {
    const v = trimOrEmpty(value);
    if (!v || (key === 'sort' && v === DEFAULT_SORT)) {
      // sort=LATEST 為預設，可不佔 URL；其餘空值刪除
      if (key === 'sort' && v === DEFAULT_SORT) {
        params.delete(key);
        return;
      }
      if (!v) {
        params.delete(key);
        return;
      }
    }
    params.set(key, v);
  };

  setOrDelete('q', next.q);
  setOrDelete('titleQuery', next.titleQuery);
  if (next.scope === 'FOLLOWING') params.set('scope', 'FOLLOWING');
  else params.delete('scope');
  setOrDelete('locationId', next.destinationKey ? '' : next.locationId);
  setOrDelete('destinationKey', next.destinationKey);
  setOrDelete('budgetMin', next.budgetMin);
  setOrDelete('budgetMax', next.budgetMax);
  const hasBudget = trimOrEmpty(next.budgetMin) !== '' || trimOrEmpty(next.budgetMax) !== '';
  setOrDelete('currency', hasBudget ? next.currency : '');
  setOrDelete('minDays', next.minDays);
  setOrDelete('maxDays', next.maxDays);
  if (['HOT', 'OLDEST', 'LIKES', 'COMMENTS', 'COPIES'].includes(trimOrEmpty(next.sort).toUpperCase())) {
    params.set('sort', trimOrEmpty(next.sort).toUpperCase());
  } else {
    params.delete('sort');
  }
  return params;
}

/** 離開 journeys tab 時清除 journey search URL keys。 */
export function clearJourneySearchParams(searchParams) {
  const params = new URLSearchParams(searchParams instanceof URLSearchParams
    ? searchParams
    : String(searchParams || ''));
  for (const key of JOURNEY_SEARCH_URL_KEYS) {
    params.delete(key);
  }
  return params;
}

export function filtersEqual(a, b) {
  const left = a || {};
  const right = b || {};
  return JOURNEY_SEARCH_URL_KEYS.every((key) => trimOrEmpty(left[key]) === trimOrEmpty(right[key]));
}
